import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Lightbox from 'yet-another-react-lightbox';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
import 'yet-another-react-lightbox/styles.css';
import { ImplementedProposalsInternational } from '../../../organisms/implemented-proposals/implemented-proposals-international';

interface GalleryItem {
  title: string;
  images: string[];
}

interface Gallery {
  title: string;
  items: GalleryItem[];
}

const GallerySection = ({
  gallery,
  onItemClick,
}: {
  gallery: Gallery;
  onItemClick: (images: string[]) => void;
}) => {
  const { t } = useTranslation(['translation']);

  return (
    <section className="px-4 sm:px-8">
      <h2 className="text-2xl sm:text-2.5xl font-semibold font-title text-secondary-500">{gallery.title}</h2>
      <ul className="mt-8 grid grid-cols-1 gap-8 sm:grid-cols-2">
        {gallery.items.map((item) => (
          <li key={item.title} onClick={() => onItemClick(item.images)} className="cursor-pointer">
            <div className="rounded-2xl">
              <div className="grid grid-cols-3 grid-rows-2 gap-2 h-80">
                <div className="col-span-2 row-span-2">
                  <img alt={item.title} src={item.images[0]} className="w-full h-full object-cover rounded-xl" />
                </div>
                <div className="col-span-1">
                  <img alt={item.title} src={item.images[1]} className="w-full h-full object-cover rounded-xl" />
                </div>
                <div className="col-span-1">
                  <img alt={item.title} src={item.images[2]} className="w-full h-full object-cover rounded-xl" />
                </div>
              </div>
              <div className="pt-3">
                <h3 className="md:text-lg font-semibold text-gray-900">{item.title}</h3>
                <p className="pt-1 text-gray-600 text-sm md:text-base">
                  {item.images.length} {t('internationalProjects.gallery.photos')}
                </p>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
};

export const InternationalProjects = () => {
  const { t } = useTranslation(['translation']);

  const [lightboxSlides, setLightboxSlides] = useState<string[]>([]);

  const classroomToolsGallery: Gallery = t('internationalProjects.gallery.classroom_tools', { returnObjects: true });
  const assembliesGallery: Gallery = t('internationalProjects.gallery.assemblies', { returnObjects: true });

  const handleItemClick = (images: string[]) => {
    setLightboxSlides(images);
  };

  return (
    <div className="flex flex-col gap-y-16 sm:gap-y-32 international-projects max-w-[1280px] mx-auto mt-24 lg:mt-36">
      <div className="z-10 w-full flex flex-col items-center w-full tools-list px-4 sm:px-8">
        <div className="relative mx-auto text-center bg-white px-18 py-7 lg:px-28 lg:py-12 rounded-card ">
          <img
            src="/assets/images/other/tools_baloons_left.png"
            alt="Baloon"
            className="absolute left-4 h-[190px] -top-10 block lg:hidden"
          />
          <span className="text-2xl sm:text-2.5xl font-semibold font-title text-secondary-500">
            {t('internationalProjects.title1')}&nbsp;
          </span>
          <img
            src="/assets/images/other/tools_baloons_right.png"
            alt="Curve"
            className="absolute h-[200px] right-4 -top-7 block lg:hidden"
          />
          <img
            src="/assets/images/tools/tools-list/title_desktop.png"
            alt="Curve"
            className="absolute max-w-[465px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 hidden lg:block"
          />
        </div>
      </div>
      <section className="px-4 sm:px-8 grid grid-cols-1 gap-y-12">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-x-16">
          <div>
            <h2 className="text-xl sm:text-2xl font-semibold font-title text-secondary-500">
              {t('internationalProjects.title2')}
            </h2>
            <p
              className="!mt-4"
              dangerouslySetInnerHTML={{
                __html: t('internationalProjects.title2_description', { interpolation: { escapeValue: false } }),
              }}
            ></p>
          </div>
          <img
            className="max-w-md mx-auto w-full"
            src="/assets/images/internationalProjects/box_agentes.webp"
            alt="Caixa Agentes"
          />
        </div>

        <ul className="flex flex-row flex-wrap justify-center gap-8">
          <li>
            <img
              className="w-[200px] h-[200px] mx-auto mb-4"
              src="/assets/images/internationalProjects/brain.webp"
              alt="Cérebro"
            />
            <p
              style={{ maxInlineSize: '33ch' }}
              dangerouslySetInnerHTML={{
                __html: t('internationalProjects.keyphrases.first'),
              }}
            ></p>
          </li>
          <li>
            <img
              className="w-[200px] h-[200px] mx-auto mb-4"
              src="/assets/images/internationalProjects/ballot.webp"
              alt="Urna"
            />
            <p
              style={{ maxInlineSize: '33ch' }}
              dangerouslySetInnerHTML={{
                __html: t('internationalProjects.keyphrases.second'),
              }}
            ></p>
          </li>
          <li>
            <img
              className="w-[200px] h-[200px] mx-auto mb-4"
              src="/assets/images/internationalProjects/megaphone.webp"
              alt="Megafone"
            />
            <p
              style={{ maxInlineSize: '33ch' }}
              dangerouslySetInnerHTML={{
                __html: t('internationalProjects.keyphrases.third'),
              }}
            ></p>
          </li>
        </ul>
        <p className="!pt-8" style={{ maxInlineSize: '60ch' }}>
          {t('internationalProjects.goal')}
        </p>
      </section>

      <section className="px-4 sm:px-8">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl sm:text-2.5xl font-semibold font-title text-secondary-500">
            {t('internationalProjects.title3')}
          </h2>
          <img src="/assets/images/internationalProjects/love_hands.svg" alt="Coração" />
        </div>
        <img className="mt-20 w-full" src="/assets/images/internationalProjects/map.webp" alt="Mapa" />
      </section>

      <GallerySection gallery={classroomToolsGallery} onItemClick={handleItemClick} />
      <GallerySection gallery={assembliesGallery} onItemClick={handleItemClick} />

      <section className="px-4 sm:px-8">
        <h2 className="text-2xl sm:text-2.5xl font-semibold font-title text-secondary-500">
          {t('internationalProjects.title5')}
        </h2>
        <div className="mt-3">{t('internationalProjects.title5_description')}</div>
        <ImplementedProposalsInternational />
      </section>

      <section className="px-4 sm:px-8">
        <div className=" max-w-[800px] m-auto text-center">
          <h2 className="text-2xl sm:text-2.5xl font-semibold font-title text-secondary-500">
            {t('internationalProjects.title6')}
          </h2>
          <div
            {...{ dangerouslySetInnerHTML: { __html: t('internationalProjects.title6_description') } }}
            className="mt-8"
          ></div>
        </div>

        <div className="flex items-center justify-center mt-16">
          <div className="w-full max-w-5xl">
            <div
              className="grid grid-cols-4 gap-4"
              onClick={() =>
                handleItemClick([
                  '/assets/images/internationalProjects/gallery/event-cerv/event_cerv_2.webp',
                  '/assets/images/internationalProjects/gallery/event-cerv/event_cerv_1.webp',
                  '/assets/images/internationalProjects/gallery/event-cerv/event_cerv_3.webp',
                ])
              }
            >
              <img
                src="/assets/images/internationalProjects/gallery/event-cerv/event_cerv_1.webp"
                alt="Person presenting"
                className="col-span-1 h-72 w-full rounded-xl object-cover shadow-md"
              />

              <img
                src="/assets/images/internationalProjects/gallery/event-cerv/event_cerv_2.webp"
                alt="Audience at event"
                className="col-span-2 h-72 w-full rounded-xl object-cover shadow-md"
              />

              <img
                src="/assets/images/internationalProjects/gallery/event-cerv/event_cerv_3.webp"
                alt="Man speaking into microphone"
                className="col-span-1 h-72 w-full rounded-xl object-cover shadow-md"
              />
            </div>

            <div className="pt-6 text-center">
              <h3 className="md:text-lg font-semibold text-gray-800">{t('internationalProjects.event_torres_novas_title')}</h3>
              <p className="pt-1 text-gray-600 text-sm md:text-base">{t('internationalProjects.photos_count', { count: 17 })}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 sm:px-8">
        <h2 className="text-2xl sm:text-2.5xl font-semibold font-title text-secondary-500">
          {t('internationalProjects.title7')}
        </h2>
        <div
          {...{ dangerouslySetInnerHTML: { __html: t('internationalProjects.title7_description') } }}
          className="mt-8"
          style={{
            maxInlineSize: '70ch',
          }}
        ></div>

        <div
          className="grid grid-cols-1 gap-4 lg:grid-cols-3 lg:grid-rows-4 lg:h-[55ch] mt-12"
          onClick={() =>
            handleItemClick([
              '/assets/images/internationalProjects/gallery/event-cerv/event_cerv_4.webp',
              '/assets/images/internationalProjects/gallery/event-cerv/event_cerv_5.webp',
              '/assets/images/internationalProjects/gallery/event-cerv/event_cerv_6.webp',
              '/assets/images/internationalProjects/gallery/event-cerv/event_cerv_7.webp',
            ])
          }
        >
          <img
            src="/assets/images/internationalProjects/gallery/event-cerv/event_cerv_4.webp"
            alt="Kids holding posters"
            className="h-64 w-full rounded-xl object-cover shadow-md lg:row-span-4 lg:h-full"
          />

          <img
            src="/assets/images/internationalProjects/gallery/event-cerv/event_cerv_5.webp"
            alt="People looking at posters"
            className="h-64 w-full rounded-xl object-cover shadow-md lg:row-span-2 lg:h-full"
          />

          <img
            src="/assets/images/internationalProjects/gallery/event-cerv/event_cerv_6.webp"
            alt="Wide shot of event hall"
            className="h-64 w-full rounded-xl object-cover shadow-md lg:col-start-2 lg:row-start-3 lg:row-span-2 lg:h-full"
          />

          <img
            src="/assets/images/internationalProjects/gallery/event-cerv/event_cerv_7.webp"
            alt="People gathered around poster"
            className="h-64 w-full rounded-xl object-cover shadow-md lg:col-start-3 lg:row-start-1 lg:row-span-4 lg:h-full"
          />
        </div>

        <div className="pt-5">
          <h3 className="md:text-lg font-semibold text-gray-900">{t('internationalProjects.poster_showcase_title')}</h3>
          <p className="pt-1 text-gray-600 text-sm md:text-base">{t('internationalProjects.photos_count', { count: 4 })}</p>
        </div>
      </section>

      <section className="px-4 sm:px-8">
        <h2 className="text-2xl sm:text-2.5xl font-semibold font-title text-secondary-500">
          {t('internationalProjects.title8')}
        </h2>
        <div
          {...{ dangerouslySetInnerHTML: { __html: t('internationalProjects.title8_description') } }}
          className="mt-8"
          style={{
            maxInlineSize: '70ch',
          }}
        ></div>
        <div
          className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:grid-rows-2 lg:h-[60ch] mt-12"
          onClick={() =>
            handleItemClick([
              '/assets/images/internationalProjects/gallery/event-cerv/event_cerv_8.webp',
              '/assets/images/internationalProjects/gallery/event-cerv/event_cerv_9.webp',
              '/assets/images/internationalProjects/gallery/event-cerv/event_cerv_10.webp',
              '/assets/images/internationalProjects/gallery/event-cerv/event_cerv_11.webp',
            ])
          }
        >
          <img
            src="/assets/images/internationalProjects/gallery/event-cerv/event_cerv_8.webp"
            alt="Kids holding posters"
            className="h-64 w-full rounded-xl object-cover shadow-md lg:h-full"
          />

          <img
            src="/assets/images/internationalProjects/gallery/event-cerv/event_cerv_9.webp"
            alt="People looking at posters"
            className="h-64 w-full rounded-xl object-cover shadow-md lg:h-full"
          />

          <img
            src="/assets/images/internationalProjects/gallery/event-cerv/event_cerv_10.webp"
            alt="Wide shot of event hall"
            className="h-64 w-full rounded-xl object-cover shadow-md lg:h-full"
          />

          <img
            src="/assets/images/internationalProjects/gallery/event-cerv/event_cerv_11.webp"
            alt="People gathered around poster"
            className="h-64 w-full rounded-xl object-cover shadow-md lg:h-full"
          />
        </div>

        <div className="pt-5">
          <h3 className="md:text-lg font-semibold text-gray-900">{t('internationalProjects.city_challenge_title')}</h3>
          <p className="pt-1 text-gray-600 text-sm md:text-base">{t('internationalProjects.photos_count', { count: 4 })}</p>
        </div>
      </section>

      <section className="px-4 sm:px-8">
        <h2 className="text-2xl sm:text-2.5xl font-semibold font-title text-secondary-500">
          {t('internationalProjects.title9')}
        </h2>
        <div
          {...{ dangerouslySetInnerHTML: { __html: t('internationalProjects.title9_description') } }}
          className="mt-8"
          style={{
            maxInlineSize: '70ch',
          }}
        ></div>

        <img
          src="/assets/images/internationalProjects/gallery/event-cerv/event_cerv_12.webp"
          alt="Kids holding posters"
          className="h-64 w-full rounded-xl object-cover shadow-md lg:h-[70ch] mt-12"
          onClick={() =>
            handleItemClick(['/assets/images/internationalProjects/gallery/event-cerv/event_cerv_12.webp'])
          }
        />

        <div className="pt-5">
          <h3 className="md:text-lg font-semibold text-gray-900">{t('internationalProjects.event_torres_novas_title')}</h3>
          <p className="pt-1 text-gray-600 text-sm md:text-base">
            {t('internationalProjects.delegation_description')}
          </p>
        </div>

        <div className="mt-12">
          <p
            {...{ dangerouslySetInnerHTML: { __html: t('internationalProjects.project_funding_description') } }}
          ></p>
        </div>

        <img
          src="/assets/images/internationalProjects/gallery/event-cerv/banner.webp"
          alt="Kids holding posters"
          className="max-h-64 w-full object-fill mt-12 mb-24"
        />
      </section>

      <Lightbox
        open={lightboxSlides.length > 0}
        close={() => setLightboxSlides([])}
        slides={lightboxSlides.map((img) => ({ src: img }))}
        plugins={[Thumbnails]}
      />
    </div>
  );
};

export default InternationalProjects;
